import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { kanbanApi } from '@/features/orcamentos/api/kanbanApi';
import { plataforma } from '@/platform';
import {
  ETAPAS_PADRAO,
  normalizarEtapas,
  type AcaoEtapa,
  type EtapaKanban,
} from '@/features/orcamentos/api/etapasKanban';

type Ctx = {
  etapas: EtapaKanban[];
  carregando: boolean;
  /** Recarrega do backend — chame após salvar a configuração. */
  recarregar: () => Promise<void>;
  /** Etapa pelo id, ou `null` quando o status não existe mais. */
  etapaDe: (id: string) => EtapaKanban | null;
  rotuloDe: (id: string) => string;
  corDe: (id: string) => string;
  temAcao: (id: string, acao: AcaoEtapa) => boolean;
};

const EtapasKanbanContext = createContext<Ctx | null>(null);

/**
 * Etapas do kanban de OS, carregadas uma vez por sessão e compartilhadas
 * por todas as telas que exibem status (kanban, edição, visualização e
 * relatórios). Enquanto a configuração não chega — ou se a chamada falhar
 * — vale o conjunto de fábrica, então nenhuma tela fica sem rótulo.
 *
 * Vale para desktop e web: quem responde `/api/kanban/etapas` é o
 * backend do alvo (IPC no Electron, HTTP no navegador).
 */
export function EtapasKanbanProvider({ children }: { children: ReactNode }) {
  const [etapas, setEtapas] = useState<EtapaKanban[]>(() => normalizarEtapas(ETAPAS_PADRAO));
  const [carregando, setCarregando] = useState(true);

  const recarregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await kanbanApi.obterEtapas();
      setEtapas(normalizarEtapas(r.etapas));
    } catch {
      // Sem configuração acessível o kanban ainda precisa desenhar: fica
      // com as etapas de fábrica até a próxima tentativa.
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  /*
   * As etapas são configuração da conta, não desta máquina: quando alguém
   * cria, renomeia ou apaga uma coluna no app web — ou em outro desktop —
   * o servidor avisa por WebSocket e a UI recarrega aqui, sem precisar de
   * F5 nem de novo login. Vale para os dois alvos: no desktop o aviso
   * chega pelo IPC do main process, no web pelo `/ws/app`.
   */
  useEffect(() => plataforma.aoAtualizarDados(() => void recarregar()), [recarregar]);

  const valor = useMemo<Ctx>(() => {
    const porId = new Map(etapas.map((e) => [e.id, e]));
    return {
      etapas,
      carregando,
      recarregar,
      etapaDe: (id) => porId.get(id) ?? null,
      rotuloDe: (id) => porId.get(id)?.rotulo ?? id,
      corDe: (id) => porId.get(id)?.cor ?? '#64748b',
      temAcao: (id, acao) => porId.get(id)?.acoes.includes(acao) ?? false,
    };
  }, [etapas, carregando, recarregar]);

  return (
    <EtapasKanbanContext.Provider value={valor}>{children}</EtapasKanbanContext.Provider>
  );
}

/**
 * Fora do provider — componentes de impressão renderizados em janela
 * própria, testes de unidade — o hook não explode: entrega as etapas de
 * fábrica. Um cupom com o rótulo padrão é melhor do que uma tela branca.
 */
const PADRAO: Ctx = (() => {
  const etapas = normalizarEtapas(ETAPAS_PADRAO);
  const porId = new Map(etapas.map((e) => [e.id, e]));
  return {
    etapas,
    carregando: false,
    recarregar: async () => undefined,
    etapaDe: (id) => porId.get(id) ?? null,
    rotuloDe: (id) => porId.get(id)?.rotulo ?? id,
    corDe: (id) => porId.get(id)?.cor ?? '#64748b',
    temAcao: (id, acao) => porId.get(id)?.acoes.includes(acao) ?? false,
  };
})();

export function useEtapasKanban(): Ctx {
  return useContext(EtapasKanbanContext) ?? PADRAO;
}
